import React, { useEffect, useState } from 'react';
import { Modal, Button, Table, Badge, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { getInventoryIssuanceSummary } from '../services/project/inventoryService';

const DistributionNode = ({ node, depth = 0 }) => {
  const [expanded, setExpanded] = useState(true);

  if (!node) return null;

  const hasChildren =
    Array.isArray(node.redistributedTo) && node.redistributedTo.length > 0;

  const indentStyle = {
    marginLeft: depth * 24,
    borderLeft: depth > 0 ? '2px solid #dee2e6' : 'none',
    paddingLeft: depth > 0 ? 12 : 0,
  };

  return (
    <div style={indentStyle} className="mb-3">
      <div className="d-flex align-items-center gap-2 mb-1">
        {depth > 0 && <span className="text-muted small">└</span>}
        {hasChildren && (
          <Button
            variant="link"
            size="sm"
            className="p-0 text-decoration-none lh-1"
            onClick={() => setExpanded((v) => !v)}
            title={expanded ? 'Collapse' : 'Expand'}
          >
            {expanded ? '▾' : '▸'}
          </Button>
        )}
        <strong>{node.fullName || node.username || `User #${node.userId}`}</strong>
        {node.aimsId && (
          <Badge bg="secondary" className="fw-normal">
            AIMS: {node.aimsId}
          </Badge>
        )}
        {node.username && (
          <span className="text-muted small">@{node.username}</span>
        )}
        {hasChildren && (
          <span className="text-muted small">
            ({node.redistributedTo.length} redistributed to)
          </span>
        )}
      </div>

      {Array.isArray(node.items) && node.items.length > 0 && (
        <Table size="sm" bordered hover responsive className="mb-2">
          <thead className="table-light">
            <tr>
              <th>Mat Code</th>
              <th>Mat Label</th>
              <th>Mat Type</th>
              <th>Description</th>
              <th className="text-end">Received</th>
              <th className="text-end">Redistributed</th>
              {depth === 0 ? (
                <>
                  <th className="text-end">Consumed (Personal)</th>
                  <th className="text-end">Consumed (Total)</th>
                  <th className="text-end">Remaining (Personal)</th>
                  <th className="text-end">Remaining (Total)</th>
                </>
              ) : (
                <>
                  <th className="text-end">Consumed</th>
                  <th className="text-end">Remaining</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {node.items.map((item, idx) => (
              <tr key={item.matCode || idx}>
                <td>{item.matCode ?? '-'}</td>
                <td>{item.matLabel ?? '-'}</td>
                <td>{item.matType ?? '-'}</td>
                <td>{item.matDescription ?? '-'}</td>
                <td className="text-end">{item.qty ?? '-'}</td>
                <td className="text-end">{item.redistributed ?? '-'}</td>
                {depth === 0 ? (
                  <>
                    <td className="text-end">{item.consumedPersonal ?? '-'}</td>
                    <td className="text-end">{item.consumedTotal ?? '-'}</td>
                    <td className="text-end">{item.remainingPersonal ?? '-'}</td>
                    <td className="text-end">
                      <span className={Number(item.remainingTotal) === 0 ? 'text-danger' : 'text-success fw-semibold'}>
                        {item.remainingTotal ?? '-'}
                      </span>
                    </td>
                  </>
                ) : (
                  <>
                    <td className="text-end">{item.consumedPersonal ?? '-'}</td>
                    <td className="text-end">
                      <span className={Number(item.remaining) === 0 ? 'text-danger' : 'text-success fw-semibold'}>
                        {item.remaining ?? '-'}
                      </span>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {hasChildren && expanded &&
        node.redistributedTo.map((child) => (
          <DistributionNode key={child.userId} node={child} depth={depth + 1} />
        ))}
    </div>
  );
};

const IssuanceHistoryModal = ({ show, giNumber, onHide }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!show || !giNumber) {
      setData(null);
      return;
    }

    const load = async () => {
      setLoading(true);
      try {
        const res = await getInventoryIssuanceSummary(giNumber);
        setData(res);
      } catch (err) {
        toast.error(
          err?.response?.data?.errorMessage || 'Failed to load issuance history'
        );
        setData(null);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [show, giNumber]);

  const issuance = data?.issuance;
  const distribution = data?.distribution;
  const summary = data?.summary;

  return (
    <Modal show={show} onHide={onHide} centered size="xl" scrollable>
      <Modal.Header closeButton>
        <Modal.Title>
          Full Distribution History{giNumber ? ` — ${giNumber}` : ''}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {loading && (
          <div className="d-flex justify-content-center py-5">
            <Spinner animation="border" variant="primary" />
          </div>
        )}

        {!loading && !data && (
          <p className="text-muted text-center py-4">No data available.</p>
        )}

        {!loading && data && (
          <>
            {issuance && (
              <div className="mb-4">
                <h6 className="fw-bold">Issuance Info</h6>
                <div className="d-flex flex-wrap gap-4 text-sm">
                  <span><strong>GI Number:</strong> {issuance.GINumber ?? '-'}</span>
                  <span><strong>GI Date:</strong> {issuance.GIDate ?? '-'}</span>
                  <span><strong>Deployment Days:</strong> {issuance.deploymentDays ?? '-'}</span>
                  <span><strong>End Date:</strong> {issuance.endDate ?? '-'}</span>
                  <span><strong>AIMS ID:</strong> {issuance.aimsId ?? '-'}</span>
                </div>
              </div>
            )}

            {summary && (
              <div className="mb-4 p-3 bg-light rounded">
                <h6 className="fw-bold mb-2">Summary</h6>
                <div className="d-flex flex-wrap gap-4">
                  <span>
                    <strong>Total Issued:</strong>{' '}
                    <Badge bg="primary">{summary.totalIssued ?? '-'}</Badge>
                  </span>
                  <span>
                    <strong>Total Distributed:</strong>{' '}
                    <Badge bg="info" text="dark">{summary.totalDistributed ?? '-'}</Badge>
                  </span>
                  <span>
                    <strong>Total Consumed:</strong>{' '}
                    <Badge bg="warning" text="dark">{summary.totalConsumed ?? '-'}</Badge>
                  </span>
                  <span>
                    <strong>Total Remaining:</strong>{' '}
                    <Badge bg="success">{summary.totalRemaining ?? '-'}</Badge>
                  </span>
                </div>
              </div>
            )}

            {distribution && (
              <div>
                <h6 className="fw-bold mb-3">Distribution Tree</h6>
                <DistributionNode node={distribution} depth={0} />
              </div>
            )}
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default IssuanceHistoryModal;
